/**
 * Admin-Karte: Pflicht-Benachrichtigungen (Joerg-Regel 2026-07-05).
 * Admin waehlt welche Slugs fuer normale Anwender NICHT abschaltbar sein
 * sollen. GF + Admin duerfen ohnehin alles fuer sich konfigurieren
 * (ArbZG § 2). Nur fuer Portal-Admins sichtbar (Route selbst prueft das,
 * hier optische Sperre + Fehler-Handling).
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, Lock, Save } from 'lucide-react';
import { api, useAuth } from '@onvice/portal-shell-api';

type Channel = 'in_app' | 'push_web' | 'push_mobile' | 'email';

interface MandatoryResponse {
  success: boolean;
  mandatory_slugs: string[];
  slug_labels: Record<string, string>;
  slug_defaults: Record<string, Channel[]>;
}

export default function NotificationMandatorySlugsCard() {
  // 2026-09-06 (A4-Beifang, HW-AD-EINSTELLUNGEN-001): isAdmin VOR dem Fetch
  // pruefen statt den 403 abzuwarten — der Admin-Call feuerte fuer jeden
  // Anwender auf /settings und verrauschte den Smoke.
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [visible, setVisible] = useState(true);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true); setMessage('');
    try {
      const r = await api.get<MandatoryResponse>('/notifications/admin/mandatory-slugs');
      setSelected(new Set(r.mandatory_slugs ?? []));
      setLabels(r.slug_labels ?? {});
    } catch (err) {
      const status = (err as { status?: number } | undefined)?.status;
      if (status === 403 || status === 401) {
        // Nicht-Admin: Card ausblenden statt Fehlermeldung.
        setVisible(false);
      } else {
        setMessage((err as Error).message || 'Fehler beim Laden');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) { setVisible(false); setLoading(false); return; }
    void load();
  }, [isAdmin, load]);

  const toggle = (slug: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });
  };

  const save = async (): Promise<void> => {
    setSaving(true); setMessage('');
    try {
      await api.put('/notifications/admin/mandatory-slugs', { slugs: Array.from(selected) });
      setMessage('Gespeichert.');
    } catch (err) {
      setMessage((err as Error).message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (!visible) return null;

  const sortedSlugs = Object.keys(labels).sort();

  return (
    <div className="bg-card border border-default rounded-xl p-4 sm:p-5 mb-4"
         data-test-anchor="pflicht-benachrichtigungen">
      <div className="flex items-center gap-2 mb-4">
        <Lock size={18} className="text-primary" />
        <h2 className="text-lg font-semibold">Pflicht-Benachrichtigungen (Admin)</h2>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Diese Ereignisse werden allen Anwendern per Web-Push zugestellt und
        koennen von ihnen NICHT abgeschaltet werden. Typisch fuer gesetzliche
        Compliance-Meldungen (ArbZG, BUrlG). Geschaeftsfuehrung und Admin
        duerfen ihre eigenen Kanaele weiterhin frei konfigurieren.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-3">
          <Loader2 size={14} className="animate-spin" /> Lade…
        </div>
      ) : (
        <>
          <ul className="space-y-1 mb-4 max-h-96 overflow-y-auto">
            {sortedSlugs.map((slug) => {
              const active = selected.has(slug);
              const label = labels[slug] ?? slug;
              return (
                <li key={slug}>
                  <label className="flex items-start gap-2 p-2 rounded-md hover:bg-background/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => toggle(slug)}
                      className="mt-1 w-4 h-4"
                    />
                    <div className="flex-1">
                      <div className="text-sm">{label}</div>
                      <div className="text-[11px] text-muted-foreground">{slug}</div>
                    </div>
                  </label>
                </li>
              );
            })}
          </ul>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => void save()}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Speichern
            </button>
            {message && <span className="text-sm text-muted-foreground">{message}</span>}
          </div>
        </>
      )}
    </div>
  );
}
